/**
 * ProductionLine 模块 API 工厂函数
 * 
 * @module api/production-line/factory
 * @description 生产线管理模块的 API 工厂函数
 * 
 * @backend backend/app/Modules/Organization/Controllers/ProductionLineController.php
 * @routes /api/v1/production-lines
 * 
 * @author YSFL Team
 * @created 2025-10-24
 * @last-updated 2025-10-24
 */

// ===== 导入类型定义 =====
import type {
  ProductionLine,
  ProductionLineQueryParams,
  ProductionLineAllQueryParams,
  CreateProductionLineForm,
  UpdateProductionLineForm,
  BatchImportProductionLineForm,
  UpdateOrderForm,
  ProductionLineFindByCodeParams,
  ProductionLineStatistics,
  Workstation,
  CalculateCapacityParams,
} from '../../types/organization';

import type { PaginationApiResponse, BatchDeleteResult, BatchImportResult } from '../../types/api';
import type { IRequestClient } from '../core/client';

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes';

/**
 * 创建 ProductionLine API 实例
 * 
 * @param client HTTP 请求客户端
 * @returns ProductionLine API 实例
 */
export function createProductionLineApi(client: IRequestClient) {
  return {
    /**
     * 获取生产线列表（分页）
     */
    getList(params?: ProductionLineQueryParams) {
      return client.get<PaginationApiResponse<ProductionLine>>(API_ROUTES.productionLine.list, { params });
    },

    /**
     * 获取所有生产线（不分页）
     */
    getAll(params?: ProductionLineAllQueryParams) {
      return client.get<ProductionLine[]>(API_ROUTES.productionLine.all, { params });
    },

    /**
     * 获取生产线详情
     */
    getDetail(id: number) {
      return client.get<ProductionLine>(API_ROUTES.productionLine.detail(id));
    },

    /**
     * 创建生产线
     */
    create(data: CreateProductionLineForm) {
      return client.post<ProductionLine>(API_ROUTES.productionLine.create, data);
    },

    /**
     * 更新生产线
     */
    update(id: number, data: UpdateProductionLineForm) {
      return client.put<ProductionLine>(API_ROUTES.productionLine.productionLines(id), data);
    },

    /**
     * 删除生产线
     */
    delete(id: number) {
      return client.delete<void>(API_ROUTES.productionLine.delete(id));
    },

    /**
     * 批量删除生产线
     */
    batchDelete(ids: number[]) {
      return client.post<BatchDeleteResult>(API_ROUTES.productionLine.batchDelete, { ids });
    },

    /**
     * 批量导入生产线
     */
    import(data: BatchImportProductionLineForm) {
      return client.post<BatchImportResult>(API_ROUTES.productionLine.import, data);
    },

    /**
     * 更新生产线排序
     */
    updateOrder(data: UpdateOrderForm) {
      return client.put<void>(API_ROUTES.productionLine.order, data);
    },

    /**
     * 根据生产线编码查询
     */
    findByCode(params: ProductionLineFindByCodeParams) {
      return client.get<ProductionLine>(API_ROUTES.productionLine.findByCode, { params });
    },

    /**
     * 获取生产线统计信息
     */
    getStatistics(id: number) {
      return client.get<ProductionLineStatistics>(API_ROUTES.productionLine.statistics(id));
    },

    /**
     * 获取生产线的工位列表
     */
    getWorkstations(id: number) {
      return client.get<Workstation[]>(API_ROUTES.productionLine.workstations(id));
    },

    /**
     * 计算生产线理论产能
     */
    calculateCapacity(id: number, params?: CalculateCapacityParams) {
      return client.get<{ lineId: number; workingHours: number; theoreticalCapacity: number }>(
        API_ROUTES.productionLine.calculateCapacity(id),
        { params }
      );
    },
  };
}

/**
 * ProductionLine API 类型
 */
export type ProductionLineApi = ReturnType<typeof createProductionLineApi>;

/**
 * 默认导出
 */
export default createProductionLineApi;

